import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { inZone } from '../../utils/companyDate';
import { formatCurrency, formatPriceOrPending, priceIsKnown, portalPriceText, pendingServicesNote } from '../../utils/formatCurrency';
import { formatPhone } from '../../utils/formatPhone';
import ServiceRequestCard from '../../components/ServiceRequestCard';
import usePullToRefresh from '../../hooks/usePullToRefresh';

const HOME_SCREEN_TIP_KEY = 'portalHomeScreenTipDismissed';

// 'ios', 'android', or null. Null on desktop, and when the portal is already
// running from a home screen icon, so the tip only shows where it can be acted on.
function detectInstallPlatform() {
  try {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (standalone) return null;
    const ua = window.navigator.userAgent || '';
    // iPadOS reports a desktop Mac user agent; the touch points give it away.
    const iPadOS = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
    if (/iPhone|iPad|iPod/.test(ua) || iPadOS) return 'ios';
    if (/Android/.test(ua)) return 'android';
  } catch (err) {
    // No tip is better than a broken dashboard.
  }
  return null;
}

function readTipDismissed() {
  try {
    return localStorage.getItem(HOME_SCREEN_TIP_KEY) === '1';
  } catch (err) {
    return false;
  }
}

function CustomerDashboard() {
  const navigate = useNavigate();
  const { customer, logout, getAuthHeader, isAuthenticated, loading: authLoading } = usePortalAuth();

  const [services, setServices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [installPlatform] = useState(detectInstallPlatform);
  const [tipDismissed, setTipDismissed] = useState(readTipDismissed);
  // getAuthHeader builds a new object on every call; the card reloads when its
  // headers change, so it is given one object per login.
  const portalHeaders = useMemo(() => (isAuthenticated ? getAuthHeader() : null), [isAuthenticated, getAuthHeader]);

  const dismissTip = () => {
    setTipDismissed(true);
    try {
      localStorage.setItem(HOME_SCREEN_TIP_KEY, '1');
    } catch (err) {
      // Storage blocked: the tip stays hidden until the page reloads.
    }
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/portal/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const loadData = useCallback(async () => {
    try {
      const [servicesRes, invoicesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/portal/services`, { headers: getAuthHeader() }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/portal/invoices`, { headers: getAuthHeader() }),
      ]);
      setServices(servicesRes.data.slice(0, 5)); // Last 5 services
      setInvoices(invoicesRes.data);
    } catch (err) {
      console.error('Failed to load portal data:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, loadData]);

  const { PullToRefreshIndicator } = usePullToRefresh(loadData);

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  // completedAt is an instant; the day shown is the company's, not the phone's.
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', inZone(customer?.timezone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }));
  };

  const unpaidInvoices = invoices.filter(i => i.paymentStatus !== 'paid' && !i.isComp);

  // An invoice with no price set used to contribute 0 to this sum while being
  // listed as Unpaid, so the balance understated what the customer owed and did
  // it silently. An unknown is not a zero: it comes OUT of the number and is
  // counted beside it, so the figure means "what we can tell you you owe" and is
  // never presented as complete when it is not.
  // priceIsKnown, not priceKnown: an unknown salt price understates the total in
  // the same way a missing base does, so both are excluded from the figure. This
  // page and the Invoices page must use the SAME rule, or a customer sees two
  // different balances on two pages of one portal.
  const pricedUnpaid = unpaidInvoices.filter(priceIsKnown);
  const pendingCount = unpaidInvoices.length - pricedUnpaid.length;
  const unpaidTotal = pricedUnpaid.reduce((sum, i) => sum + i.total, 0);
  // When nothing is priced, $0.00 is the exact lie being removed, so the balance
  // shows the same "Not yet priced" as every other surface and the sub-line
  // carries the count.
  const balanceKnown = !(pendingCount > 0 && pricedUnpaid.length === 0);
  const pendingNote = pendingServicesNote(unpaidInvoices.filter(i => !priceIsKnown(i)));

  if (authLoading || (!isAuthenticated && !loading)) {
    return (
      <div className="portal-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <PullToRefreshIndicator />
      <header className="portal-header">
        <div className="portal-header-content">
          <div className="portal-brand">
            <h1>{customer?.company?.name || 'Customer Portal'}</h1>
          </div>
          <nav className="portal-nav">
            <Link to="/portal" className="portal-nav-link active">Dashboard</Link>
            <Link to="/portal/services" className="portal-nav-link">Services</Link>
            <Link to="/portal/invoices" className="portal-nav-link">Invoices</Link>
            <button onClick={handleLogout} className="portal-nav-link logout">Logout</button>
          </nav>
        </div>
      </header>

      <main className="portal-main">
        <div className="portal-container">
          <div className="portal-welcome">
            <h2>Welcome back, {customer?.firstName}!</h2>
            <p>Here's an overview of your account.</p>
          </div>

          {installPlatform && !tipDismissed && (
            <div className="home-screen-tip">
              <div className="home-screen-tip-header">
                <strong>Add this portal to your home screen</strong>
                <button type="button" className="home-screen-tip-close" onClick={dismissTip} aria-label="Dismiss">×</button>
              </div>
              <p>Check your services and invoices with one tap. The icon is named My Snow Service.</p>
              {installPlatform === 'ios' ? (
                <ol>
                  <li>In Safari, tap the <strong>Share</strong> button (the square with an arrow pointing up)</li>
                  <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                  <li>Tap <strong>Add</strong></li>
                </ol>
              ) : (
                <ol>
                  <li>In Chrome, tap the menu (the three dots, top right)</li>
                  <li>Tap <strong>Add to Home screen</strong> or <strong>Install app</strong></li>
                  <li>Tap <strong>Install</strong> or <strong>Add</strong> to confirm</li>
                </ol>
              )}
              <button type="button" className="home-screen-tip-dismiss" onClick={dismissTip}>Got it</button>
            </div>
          )}

          {/* Stats Cards */}
          <div className="portal-stats">
            <div className="stat-card">
              <div className="stat-value">{services.length > 0 ? services.length : '-'}</div>
              <div className="stat-label">Recent Services</div>
            </div>
            <div className={`stat-card ${unpaidTotal > 0 || pendingCount > 0 ? 'warning' : ''}`}>
              <div className={`stat-value${balanceKnown ? '' : ' price-pending'}`}>
                {formatPriceOrPending(unpaidTotal, balanceKnown)}
              </div>
              <div className="stat-label">Outstanding Balance</div>
              {/* "plus" only when there is a figure to add to. When the value itself
                  already reads "Not yet priced", the sub-line carries the count alone. */}
              {pendingCount > 0 && (
                <div className="stat-note">{balanceKnown ? `plus ${pendingNote}` : pendingNote}</div>
              )}
            </div>
            <div className="stat-card">
              <div className="stat-value">{invoices.filter(i => i.paymentStatus === 'paid').length}</div>
              <div className="stat-label">Paid Invoices</div>
            </div>
          </div>

          {/* Unpaid Invoices Alert */}
          {unpaidInvoices.length > 0 && (
            <div className="portal-alert">
              <div className="alert-content">
                <span className="alert-icon">!</span>
                <div className="alert-text">
                  <strong>You have {unpaidInvoices.length} unpaid invoice{unpaidInvoices.length > 1 ? 's' : ''}</strong>
                  {/* "totaling" needs a total. With nothing priced there is none, so the
                      line carries the count on its own rather than claiming a figure. */}
                  <span>
                    {balanceKnown
                      ? ` totaling ${formatCurrency(unpaidTotal)}${pendingCount > 0 ? `, plus ${pendingNote}` : ''}`
                      : ` ${pendingNote}`}
                  </span>
                </div>
              </div>
              <Link to="/portal/invoices" className="alert-action">View Invoices</Link>
            </div>
          )}

          {/* Request service (on-call customers), or the one-line seasonal note.
              The server decides which, or neither (migration 102). */}
          {portalHeaders && (
            <ServiceRequestCard
              loadUrl={`${process.env.REACT_APP_API_URL}/api/portal/service-requests`}
              createUrl={`${process.env.REACT_APP_API_URL}/api/portal/service-requests`}
              cancelUrl={(id) => `${process.env.REACT_APP_API_URL}/api/portal/service-requests/${id}/cancel`}
              headers={portalHeaders}
              title="Request service"
              intro="You are on call: pick the days you want us to come. You can choose several."
              submitLabel="Request service"
              variant="portal"
            />
          )}

          {/* Recent Services */}
          <div className="portal-section">
            <div className="section-header">
              <h3>Recent Services</h3>
              <Link to="/portal/services" className="section-link">View All</Link>
            </div>
            {loading ? (
              <div className="portal-loading-inline">Loading...</div>
            ) : services.length === 0 ? (
              <div className="portal-empty">No services yet.</div>
            ) : (
              <div className="services-list">
                {services.map(service => (
                  <div key={service.id} className="service-item">
                    <div className="service-date">{formatDate(service.completedAt)}</div>
                    <div className="service-details">
                      <span className="service-route">{service.baseLabel || 'Service'}</span>
                      {service.photoCount > 0 && (
                        <span className="photo-badge">📷 {service.photoCount}</span>
                      )}
                    </div>
                    <div className="service-price">
                      {service.isComp ? (
                        <span className="comp-tag">COMP</span>
                      ) : service.pendingDepth === true || service.priceKnown === false ? (
                        <span className="price-pending">{portalPriceText(service, service.price)}</span>
                      ) : (
                        `${formatCurrency(service.price)}`
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact Info. Shown only when there is a way to make contact: a card
              holding a heading and nothing else is worse than no card. The login
              response carries no company, so this also waits for /me. */}
          {(customer?.company?.phone || customer?.company?.email) && (
            <div className="portal-section contact-section">
              <h3>Contact Us</h3>
              {customer.company.name && (
                <div className="contact-company">{customer.company.name}</div>
              )}
              <div className="contact-info">
                {customer.company.phone && (
                  <a href={`tel:${customer.company.phone}`} className="contact-item">
                    <span className="contact-icon">📞</span>
                    <span>{formatPhone(customer.company.phone)}</span>
                  </a>
                )}
                {customer.company.email && (
                  <a href={`mailto:${customer.company.email}`} className="contact-item">
                    <span className="contact-icon">✉️</span>
                    <span>{customer.company.email}</span>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <style>{`
        .portal-page {
          min-height: 100vh;
          background: #f1f5f9;
        }
        .portal-loading {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
        }
        .portal-loading .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .portal-header {
          background: linear-gradient(135deg, var(--color-primary, #1e88e5) 0%, var(--color-primary-dark, #1565c0) 100%);
          color: white;
          padding: 16px 20px;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .portal-header-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .portal-brand h1 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }
        .portal-nav {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .portal-nav-link {
          background: rgba(255,255,255,0.1);
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 16px;
          border-radius: 6px;
          text-decoration: none;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.15s;
        }
        .portal-nav-link:hover, .portal-nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .portal-nav-link.logout {
          background: rgba(239,68,68,0.2);
          color: #fca5a5;
        }
        .portal-nav-link.logout:hover {
          background: rgba(239,68,68,0.3);
          color: white;
        }
        .portal-main {
          padding: 24px 20px;
        }
        .portal-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        .portal-welcome {
          margin-bottom: 24px;
        }
        .portal-welcome h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 4px 0;
        }
        .portal-welcome p {
          color: #64748b;
          margin: 0;
        }
        .home-screen-tip {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 24px;
          color: #1e3a8a;
        }
        .home-screen-tip-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }
        .home-screen-tip p {
          margin: 6px 0 8px 0;
        }
        .home-screen-tip ol {
          margin: 0 0 12px 0;
          padding-left: 20px;
        }
        .home-screen-tip li {
          margin-bottom: 4px;
        }
        .home-screen-tip-close {
          background: none;
          border: none;
          font-size: 1.4rem;
          line-height: 1;
          color: #1e3a8a;
          cursor: pointer;
          padding: 0 4px;
        }
        .home-screen-tip-dismiss {
          background: #1e88e5;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-weight: 600;
          cursor: pointer;
        }
        .portal-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .stat-card.warning {
          border-left: 4px solid #dc2626;
        }
        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #0f172a;
        }
        .stat-label {
          font-size: 0.875rem;
          color: #64748b;
          margin-top: 4px;
        }
        /* Grey and italic, so a short phrase is never mistaken for a figure. The
           1.75rem of .stat-value is dropped: a large italic phrase looks like a
           headline rather than the note it is. */
        .price-pending {
          color: #64748b;
          font-style: italic;
          font-weight: 500;
        }
        .stat-value.price-pending {
          font-size: 1.05rem;
          font-weight: 600;
        }
        .stat-note {
          font-size: 0.8125rem;
          color: #64748b;
          margin-top: 6px;
          line-height: 1.35;
        }
        .portal-alert {
          background: #fef3c7;
          border: 1px solid #fcd34d;
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        .alert-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .alert-icon {
          width: 28px;
          height: 28px;
          background: #f59e0b;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1rem;
        }
        .alert-text {
          color: #92400e;
          font-size: 0.95rem;
        }
        .alert-action {
          background: #f59e0b;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9rem;
        }
        .alert-action:hover {
          background: #d97706;
        }
        .portal-section {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          margin-bottom: 24px;
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .section-header h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
        }
        .section-link {
          color: #3b82f6;
          text-decoration: none;
          font-size: 0.9rem;
        }
        .section-link:hover {
          text-decoration: underline;
        }
        .portal-loading-inline {
          padding: 20px;
          text-align: center;
          color: #64748b;
        }
        .portal-empty {
          padding: 20px;
          text-align: center;
          color: #64748b;
        }
        .services-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .service-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
        }
        .service-date {
          font-size: 0.875rem;
          color: #64748b;
          min-width: 100px;
        }
        .service-details {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .service-route {
          font-weight: 500;
          color: #0f172a;
        }
        .photo-badge {
          background: rgba(59,130,246,0.1);
          color: #3b82f6;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 0.75rem;
        }
        .service-price {
          font-weight: 600;
          color: #0f172a;
        }
        .comp-tag {
          background: #dbeafe;
          color: #1d4ed8;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .contact-section h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 16px 0;
        }
        .contact-company {
          font-weight: 500;
          color: #0f172a;
          margin-bottom: 12px;
        }
        .contact-info {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }
        .contact-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #3b82f6;
          text-decoration: none;
          font-size: 0.95rem;
        }
        .contact-item:hover {
          text-decoration: underline;
        }
        .contact-icon {
          font-size: 1.1rem;
        }

        @media (max-width: 640px) {
          .portal-header-content {
            flex-direction: column;
            align-items: flex-start;
          }
          .service-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          .service-details {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>
    </div>
  );
}

export default CustomerDashboard;
